import type { ReactElement } from 'react'
import React, { useEffect, useRef, useState } from 'react'

import { useButton } from '@react-aria/button'
import { useFocusRing } from '@react-aria/focus'
import type { PressEvents } from '@react-types/shared'
import { IMAGE } from '@ui-kit/assets/images/index.web'
import Icon from '@ui-kit/components/SvgIcon/index.web'
import { caption, h4 } from '@ui-kit/core/typography'
import { borderColor } from '@ui-kit/styles/colors'

import { accordionItem, accordionListContainer } from './styles'
import type { AccordionItem, Props } from './types'

const AccordionListItem = (
  props: AccordionItem &
    Pick<Props, 'borderColor'> & {
      isExpanded: boolean
      id: string
      onPress: PressEvents['onPress']
      rowDividerBordered?: boolean
    }
) => {
  const ref = useRef<HTMLButtonElement>(null)
  const ariaControls = props.content ? `${props.id}-content` : undefined
  const { buttonProps } = useButton(
    { ...props, 'aria-controls': ariaControls },
    ref as React.RefObject<Element>
  )
  const { focusProps } = useFocusRing()
  const isExpandable = props.content !== undefined
  const { container, icon, triggerTextContainer, trigger, content } = accordionItem({
    isExpandable,
    isExpanded: props.isExpanded,
    hasIcon: Boolean(props.icon),
  })
  const chevronIcon = props.isExpanded ? 'chevronUp_24' : 'chevronDown_24'

  return (
    <div
      key={props.key}
      className={container({
        className: borderColor({ color: props.borderColor }),
        rowDividerBordered: props.rowDividerBordered,
      })}
    >
      <button className={trigger()} ref={ref} {...buttonProps} {...focusProps}>
        {props.icon && <div className={icon()}>{props.icon}</div>}
        <div className={triggerTextContainer()}>
          <div className={h4()}>{props.title}</div>
          {props.subtitle && (
            <div className={caption({ emphasis: 'medium' })}>{props.subtitle}</div>
          )}
        </div>
        {props.headingRightContent}
        {isExpandable && (
          <Icon imgSrc={IMAGE.icons.small.action[chevronIcon]} className="self-start" />
        )}
      </button>
      {props.content && (
        <div
          role="region"
          className={content()}
          aria-labelledby={props.id}
          id={`${props.id}-content`}
        >
          {props.content}
        </div>
      )}
    </div>
  )
}

const AccordionList = (props: Props): ReactElement => {
  const [accordionState, setAccordionState] = useState<boolean[]>([])

  useEffect(() => {
    const initialAccordionState = props.data.map((item) => Boolean(item.isExpandedByDefault))
    setAccordionState(initialAccordionState)
  }, [props.data.length, props.data])

  const toggleItem = (indexToToggle: number) => {
    setAccordionState(
      accordionState.map(
        (_current, currentIndex) => currentIndex === indexToToggle && !accordionState[currentIndex]
      )
    )
    if (props.onItemToggle) {
      props.onItemToggle(indexToToggle, !accordionState[indexToToggle])
    }
  }

  const container = accordionListContainer({
    className: Boolean(props.borderColor) && borderColor({ color: props.borderColor }),
    bordered: Boolean(props.borderColor) && !props.rowDividerBordered,
    rowDividerBordered: Boolean(props.borderColor) && props.rowDividerBordered,
  })

  return (
    <div className={container}>
      {props.data.map((item, index) => {
        const id = `accordion${index}id`
        return (
          <AccordionListItem
            {...item}
            isExpanded={accordionState[index]}
            id={id}
            onPress={() => toggleItem(index)}
            borderColor={props.borderColor}
            key={item.key}
            rowDividerBordered={props.rowDividerBordered}
          />
        )
      })}
    </div>
  )
}

export default AccordionList
